/**
 * De quem são as colunas da lista de Itens (SPEC-20261103-1217).
 *
 * Pedido do Cristian, 2026-11-03: "as configurações de colunas devem estar
 * atreladas ao filtro criado (...) e quando se cria em todos os itens, que seria
 * sem filtro, ai ela fica gravada para sem filtro sendo para todos ou somente
 * para pessoa que colocou".
 *
 * Duas fontes, então:
 *
 * - FILTRO com colunas: as colunas dele, na ordem dele. A largura continua da
 *   pessoa — é ajuste de tela, não parte do recorte.
 * - SEM FILTRO: as colunas da pessoa, mais as que alguém pôs "para todos". Quem
 *   recebe uma coluna "para todos" e a esconde continua escondendo: o estado
 *   guarda quais já recebeu (`todos`), e só as que ainda não recebeu ligam.
 */
import type { EstadoColuna } from './colunas-da-tabela.ts'

/** O estado da pessoa com as colunas do filtro: ligadas na ordem dele, o resto desligado. */
export function aplicarColunasDoFiltro(cols: readonly EstadoColuna[], doFiltro: readonly string[]): EstadoColuna[] {
  const porChave = new Map(cols.map((c) => [c.k, c]))
  // o Título vem sempre primeiro: é a âncora da linha, com ou sem filtro
  const ordem = [...new Set(['titulo', ...doFiltro])].filter((k) => porChave.has(k))
  const ligadas = ordem.map((k) => ({ ...porChave.get(k)!, on: true }))
  const resto = cols.filter((c) => !ordem.includes(c.k)).map((c) => ({ ...c, on: false }))
  return [...ligadas, ...resto]
}

/** O que vai para o filtro: as ligadas, na ordem, sem o Título (que é fixo). */
export function colunasLigadas(cols: readonly EstadoColuna[]): string[] {
  return cols.filter((c) => c.on && c.k !== 'titulo').map((c) => c.k)
}

/**
 * Entrega as colunas "para todos" a quem abre a lista.
 *
 * - coluna que entrou na lista e a pessoa ainda não recebeu: liga e anota;
 * - coluna que saiu da lista e a pessoa tinha recebido: desliga e esquece.
 *
 * Coluna que o catálogo ainda não conhece (campo complementar que chega por
 * outra consulta) não é anotada: senão ela nunca mais seria entregue.
 */
export function receberParaTodos(
  cols: readonly EstadoColuna[],
  recebidas: readonly string[] | undefined,
  paraTodos: readonly string[],
): { cols: EstadoColuna[]; recebidas: string[]; mudou: boolean } {
  const ja = new Set(recebidas ?? [])
  const conhecidas = new Set(cols.map((c) => c.k))
  const ligar = paraTodos.filter((k) => !ja.has(k) && conhecidas.has(k) && k !== 'titulo')
  const desligar = [...ja].filter((k) => !paraTodos.includes(k))
  if (ligar.length === 0 && desligar.length === 0) {
    return { cols: [...cols], recebidas: [...ja], mudou: false }
  }
  const novas = cols.map((c) =>
    ligar.includes(c.k) ? { ...c, on: true } : desligar.includes(c.k) ? { ...c, on: false } : c,
  )
  return {
    cols: novas,
    recebidas: [...[...ja].filter((k) => !desligar.includes(k)), ...ligar],
    mudou: true,
  }
}

/** Colunas que acabaram de ser ligadas — as que a pergunta "para quem?" trata. */
export function colunasNovas(antes: readonly EstadoColuna[], depois: readonly EstadoColuna[]): string[] {
  const ligadasAntes = new Set(antes.filter((c) => c.on).map((c) => c.k))
  return depois.filter((c) => c.on && !ligadasAntes.has(c.k)).map((c) => c.k)
}
